// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Stock, Bom } from "../stock/index.js"

// @@imports-module
import { TapeCalculator } from "./Tape.js"

// @@imports-types
import {
    Config,
    EdgeType,
    EDGE_TYPES,
    PATCH_SIZES
} from "../types/index.js"

// @@body
/**
 * @todo Complete method documentation including inline comments, jsdoc
 * annotations, and class description.
 * @todo Check that all error throws use decorated error format.
 * @todo Remove config property if unused.
 */
class CornerCalculator {
    #stock
    #config
    #tapes
    #bom = new Bom()

    /**
     * 
     * @param {Stock} stock 
     * @param {Config} config 
     * @param {TapeCalculator} tapeCalculator
     */
    constructor(stock, config, tapeCalculator) {
        this.#stock = stock
        this.#config = config
        this.#tapes = tapeCalculator
    }

    /**
     * 
     * @param {EdgeType} edgeType 
     * @param {EdgeType} nextEdgeType 
     * @param {number} angle 
     */
    addCorner(edgeType, nextEdgeType, angle) {
        switch (edgeType) {
            case EDGE_TYPES.houseWall:
            case EDGE_TYPES.parapetWall:
            case EDGE_TYPES.pitchRoofOn:
                return this.#cornerResolverHigh(edgeType, nextEdgeType, angle)
            case EDGE_TYPES.gutterDrip:
            case EDGE_TYPES.raisedKerb:
                return this.#cornerResolverFlat(edgeType, nextEdgeType, angle)
            default:
                return this.#cornerResolverLow(nextEdgeType)
        }
    }

    /**
     * 
     * @param {EdgeType} edgeType 
     * @param {EdgeType} nextEdgeType 
     * @param {number} angle
     */
    #cornerResolverHigh(edgeType, nextEdgeType, angle) {
        switch (edgeType) {
            case EDGE_TYPES.houseWall:
            case EDGE_TYPES.parapetWall:
                switch (nextEdgeType) {
                    case EDGE_TYPES.houseWall:
                    case EDGE_TYPES.parapetWall:
                        return this.#addWallCornerPatch(angle)
                    default:
                        return this.#tapes.addPatch(PATCH_SIZES.medium)
                }
            default:
                return this.#tapes.addPatch(PATCH_SIZES.medium)
        }
    }

    /**
     * 
     * @param {EdgeType} edgeType 
     * @param {EdgeType} nextEdgeType 
     * @param {number} angle 
     */
    #cornerResolverFlat(edgeType, nextEdgeType, angle) {
        switch (edgeType) {
            case EDGE_TYPES.gutterDrip:
                switch (nextEdgeType) {
                    case EDGE_TYPES.gutterDrip:
                        return this.#addGutterCorner(angle)
                    case EDGE_TYPES.raisedKerb:
                        return this.#addKerbCorner(angle)
                    case EDGE_TYPES.houseWall:
                    case EDGE_TYPES.parapetWall:
                    case EDGE_TYPES.pitchRoofOn:
                        return this.#tapes.addPatch(PATCH_SIZES.medium)
                    default:
                        return null
                }
            case EDGE_TYPES.raisedKerb:
                switch (nextEdgeType) {
                    case EDGE_TYPES.gutterDrip:
                    case EDGE_TYPES.raisedKerb:
                        return this.#addKerbCorner(angle)
                    case EDGE_TYPES.houseWall:
                    case EDGE_TYPES.parapetWall:
                    case EDGE_TYPES.pitchRoofOn:
                        return this.#tapes.addPatch(PATCH_SIZES.medium)
                    default:
                        return null
                }
            default:
                return null
        }
    }

    /**
     * 
     * @param {EdgeType} nextEdgeType 
     */
    #cornerResolverLow(nextEdgeType) {
        switch (nextEdgeType) {
            case EDGE_TYPES.houseWall:
            case EDGE_TYPES.parapetWall:
            case EDGE_TYPES.pitchRoofOn:
                return this.#tapes.addPatch(PATCH_SIZES.medium)
            default:
                return null
        }
    }

    /**
     * 
     * @param {number} angle 
     */
    #addWallCornerPatch(angle) {
        angle === Math.PI / 2
            ? this.#tapes.addPatch(PATCH_SIZES.extraSmall)
            : this.#tapes.addPatch(PATCH_SIZES.medium)
    }

    /**
     * 
     * @param {number} angle 
     */
    #addGutterCorner(angle) {
        if (angle === Math.PI) { return this.#tapes.addPatch(PATCH_SIZES.medium) }
        else {

        }
        const product = this.#stock.getCorners(EDGE_TYPES.gutterDrip, angle)[0]
        if (!product) { throw new Error() }

        this.#bom.addLineItem(product, 1)
    }

    /**
     * 
     * @param {number} angle 
     */
    #addKerbCorner(angle) {
        if (angle === Math.PI) { return this.#tapes.addPatch(PATCH_SIZES.medium) }

        const product = this.#stock.getCorners(EDGE_TYPES.raisedKerb, angle)[0]
        if (!product) { throw new Error() }

        this.#bom.addLineItem(product, 1)
    }

    get bom() {
        return this.#bom
    }
}

// @@exports
export { CornerCalculator }
